import React from 'react';
import { FiNavigation } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const DistanceRateLesson = () => {
  const sections = [
    {
      id: 'basic-distance',
      title: 'Basic Distance-Rate-Time',
      content: {
        introduction:
          'Distance, rate, and time problems are based on the fundamental relationship: Distance = Rate × Time (D = rt). This formula is the foundation for all motion problems on the GRE.',

        concepts: [
          {
            title: 'The Fundamental Formula',
            explanation:
              '§\\text{Distance} = \\text{Rate} \\times \\text{Time}§. This formula is the foundation for all motion problems.',
            example: {
              problem:
                'A car travels at 60 miles per hour for 3 hours. How far does it go?',
              solution: [
                'Rate = 60 mph',
                'Time = 3 hours',
                'Distance = §60 \\times 3 = 180§ miles',
              ],
            },
          },
          {
            title: 'Finding Rate',
            explanation:
              'You can rearrange the formula to solve for rate: §\\text{Rate} = \\frac{\\text{Distance}}{\\text{Time}}§.',
            example: {
              problem:
                'A train travels 240 kilometers in 4 hours. What is its average speed?',
              solution: [
                'Distance = 240 km, Time = 4 hours',
                'Rate = §\\frac{240}{4} = 60§ km/h',
              ],
            },
          },
          {
            title: 'Finding Time',
            explanation:
              'Similarly, to find time: §\\text{Time} = \\frac{\\text{Distance}}{\\text{Rate}}§.',
            example: {
              problem: 'How long does it take to drive 300 miles at 60 mph?',
              solution: [
                'Distance = 300 miles, Rate = 60 mph',
                'Time = §\\frac{300}{60} = 5§ hours',
              ],
            },
          },
          {
            title: 'Unit Conversion',
            explanation:
              'Always ensure units are consistent. Convert minutes to hours, or km to miles, as needed.',
            example: {
              problem:
                'A runner runs at §10§ km/h for 90 minutes. What is the distance in km?',
              solution: [
                'Convert 90 minutes to hours: §\\frac{90}{60} = 1.5§ hours',
                'Distance = §10§ km/h §\\times 1.5 = 15§ km',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'relative-motion',
      title: 'Relative Motion',
      content: {
        introduction:
          'Relative motion problems involve two or more objects moving. Key scenarios include objects moving in the same direction, opposite directions, or meeting/overtaking problems.',

        concepts: [
          {
            title: 'Opposite Directions (Head-On)',
            explanation:
              'When objects move toward each other, their relative speed is the sum of their speeds. §\\text{Distance covered} = (\\text{Speed}_A + \\text{Speed}_B) \\times \\text{Time}§.',
            example: {
              problem:
                'Two trains start 300 miles apart and travel toward each other. Train A is at 40 mph, Train B at 60 mph. When do they meet?',
              solution: [
                '§Speed_A = 40§ mph, §Speed_B = 60§ mph',
                'Relative speed = §40 + 60 = 100§ mph',
                'Distance = 300 miles',
                'Time = §\\frac{\\text{Distance}}{\\text{Relative speed}} = \\frac{300}{100} = 3§ hours',
              ],
            },
          },
          {
            title: 'Same Direction (Overtaking)',
            explanation:
              'When one object chases another, the relative speed is the difference in speeds. §\\text{Time} = \\frac{\\text{Initial Distance}}{\\text{Speed}_{\\text{faster}} - \\text{Speed}_{\\text{slower}}}§.',
            example: {
              problem:
                'Car A leaves at 40 mph. One hour later, Car B starts from the same place at 50 mph. When does Car B catch A?',
              solution: [
                'Initial distance = §40 \\text{ miles} \\times 1 \\text{ hour} = 40§ miles',
                'Relative speed = §50 - 40 = 10§ mph',
                'Time to catch up = §\\frac{40}{10} = 4§ hours',
                'Car B catches A after 4 hours.',
              ],
            },
          },
          {
            title: 'Round Trip with Different Speeds',
            explanation:
              'For round trips, use the average speed formula: §\\text{Average Speed} = \\frac{2 \\times \\text{Speed}_1 \\times \\text{Speed}_2}{\\text{Speed}_1 + \\text{Speed}_2}§ (for equal distances).',
            greTrap:
              'Never average the two speeds unless the time spent at each speed is the same. For equal distances, use total distance divided by total time.',
            example: {
              problem:
                'A person drives to work at 40 mph and returns at 60 mph. What is the average speed for the round trip?',
              solution: [
                'Let distance one way be D.',
                'Time to work = §\\frac{D}{40}§',
                'Time to return = §\\frac{D}{60}§',
                'Total distance = 2D',
                'Total time = §\\frac{D}{40} + \\frac{D}{60} = \\frac{3D+2D}{120} = \\frac{5D}{120}§',
                'Average speed = §\\frac{2D}{\\frac{5D}{120}} = \\frac{2D \\times 120}{5D} = 48§ mph',
                'Note: This is NOT §\\frac{40+60}{2}§.',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'multiple-legs',
      title: 'Multi-Leg Journeys',
      content: {
        introduction:
          'Many GRE problems involve trips with multiple segments at different speeds. The key is to calculate distance or time for each segment separately, then combine.',

        concepts: [
          {
            title: 'Different Speeds for Parts of Trip',
            explanation:
              'Calculate distance for each segment and add them up: §D_{\\text{total}} = D_1 + D_2§.',
            example: {
              problem:
                'A cyclist rides 2 hours at 15 mph, then 3 hours at 20 mph. What is the total distance?',
              solution: [
                'Segment 1: §D_1 = 15§ miles/hr §\\times 2 hr = 30§ miles',
                'Segment 2: §D_2 = 20§ miles/hr §\\times 3 hr = 60§ miles',
                'Total distance = §30 + 60 = 90§ miles',
              ],
            },
          },
          {
            title: 'Average Speed Over Multiple Segments',
            explanation:
              '§\\text{Average Speed} = \\frac{\\text{Total Distance}}{\\text{Total Time}}§. This is a weighted average and not a simple mean of the speeds.',
            example: {
              problem:
                'A train travels 100 miles at 50 mph, then 150 miles at 75 mph. What is its average speed?',
              solution: [
                'Time 1 = §\\frac{100}{50} = 2§ hours',
                'Time 2 = §\\frac{150}{75} = 2§ hours',
                'Total distance = 100 + 150 = 250 miles',
                'Total time = 2 + 2 = 4 hours',
                'Average speed = §\\frac{250}{4} = 62.5§ mph',
              ],
            },
          },
          {
            title: 'Partial Distance Problems',
            explanation:
              'Sometimes you need to work with partial trips. Use the D=RT formula for each part, and set up equations to find what you know.',
            example: {
              problem:
                'A 420-mile trip took 7 hours. The first part was at 50 mph, the rest at 65 mph. How long was the first part?',
              solution: [
                'Let t be time for first part. Then 7-t is time for second part.',
                '§50t + 65(7-t) = 420§',
                '§50t + 455 - 65t = 420§',
                '§-15t = -35§',
                '§t = \\frac{35}{15} \\approx 2.33§ hours at 50 mph. §4.67§ hours at 65mph',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'advanced-scenarios',
      title: 'Advanced Scenarios',
      content: {
        introduction:
          'GRE often tests complex scenarios including circular tracks, boats in currents, or planes with wind. These require careful analysis of how speeds combine.',

        concepts: [
          {
            title: 'Circular Track Problems',
            explanation:
              'On circular tracks, consider relative speeds and when objects lap each other. Meeting frequency depends on combined vs relative speeds.',
            example: {
              problem:
                'Two runners run on a 400m circular track in opposite directions at §5 \\frac{m}{s}§ and §3 \\frac{m}{s}§. How often do they meet?',
              solution: [
                'Combined speed = §5+3 = 8§ m/s',
                'Time to meet = §\\frac{\\text{Distance}}{\\text{Speed}} = \\frac{400}{8} = 50§ seconds',
                'They meet every 50 seconds.',
              ],
            },
          },
          {
            title: 'Current/Wind Problems',
            explanation:
              '§\\text{Speed}_{\\text{downstream}} = \\text{Speed}_{\\text{boat}} + \\text{Speed}_{\\text{current}}§. §\\text{Speed}_{\\text{upstream}} = \\text{Speed}_{\\text{boat}} - \\text{Speed}_{\\text{current}}§.',
            example: {
              problem:
                'A boat travels 40 km downstream in 2 hours and returns in 4 hours. What is the boat speed in still water?',
              solution: [
                'Downstream: §v+c = \\frac{40}{2} = 20§ km/h',
                'Upstream: §v-c = \\frac{40}{4} = 10§ km/h',
                'Add the equations: §2v = 30§, so §v = 15§ km/h',
                'Boat speed is 15 km/h.',
              ],
            },
          },
          {
            title: 'Meeting Point Problems',
            explanation:
              'When two people start miles apart and walk towards each other, the distance each walks will add up to be the total distance.',
            example: {
              problem:
                'Two people start 180 miles apart and walk towards each other. One walks at 4 mph, the other at 5 mph. When do they meet?',
              solution: [
                'Relative speed = §4+5 = 9§ mph',
                'Time = §\\frac{180}{9} = 20§ hours',
                'Distance from start for person 1: §4 \\times 20 = 80§ miles from start',
                'Distance from start for person 2: §5 \\times 20 = 100§ miles from start',
              ],
            },
          },
          {
            title: 'Speed Increase/Decrease',
            explanation:
              'Calculate the time for one scenario and then for the other. Find the difference between the two to see how much time is saved.',
            example: {
              problem:
                'A car traveling at 60 mph for a 300-mile trip increases speed to 75 mph for the second half. How much time is saved?',
              solution: [
                'First half: §150 \\text{ miles at } 60 \\text{ mph} = 2.5§ hours',
                'Second half: §150 \\text{ miles at } 75 \\text{ mph} = 2§ hours',
                'Total time with increase = 4.5 hours',
                'Original time: §\\frac{300}{60} = 5§ hours',
                'Time saved = §5 - 4.5 = 0.5§ hours = 30 minutes',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A car travels 240 miles at a constant speed of 60 mph. How many hours does the trip take?',
      options: ['3 hours', '3.5 hours', '4 hours', '4.5 hours', '5 hours'],
      correct: 2,
      explanation: 'Time = Distance / Rate = 240 / 60 = 4 hours.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'Two trains start 450 miles apart and travel toward each other. One train travels at 50 mph and the other at 70 mph. How long until they meet?',
      options: ['3 hours', '3.5 hours', '3.75 hours', '4 hours', '4.5 hours'],
      correct: 2,
      explanation:
        'Combined speed = §50 + 70 = 120§ mph. Time = §450 / 120 = 3.75§ hours.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'A cyclist rides for 3 hours at 12 mph and then 2 hours at 15 mph. What is the total distance covered?',
      options: ['60 miles', '66 miles', '70 miles', '72 miles', '75 miles'],
      correct: 1,
      explanation:
        '§Distance₁ = 12 \\times 3 = 36§ miles. §Distance₂ = 15 \\times 2 = 30§ miles. Total = §36 + 30 = 66§ miles.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'A runner completes a §10§ km race in 50 minutes. What is their average speed in km/h?',
      options: [
        '§10§ km/h',
        '§11§ km/h',
        '§12§ km/h',
        '§13§ km/h',
        '§14§ km/h',
      ],
      correct: 2,
      explanation:
        '50 minutes = §50/60§ hours = §5/6§ hours. Speed = §10 / (5/6) = 10 \\times 6/5 = 12§ km/h.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'A boat travels §60§ km downstream in 3 hours and returns upstream in 5 hours. What is the speed of the current?',
      options: ['§2§ km/h', '§3§ km/h', '§4§ km/h', '§5§ km/h', '§6§ km/h'],
      correct: 2,
      explanation:
        'Downstream speed = §60/3 = 20§ km/h. Upstream speed = §60/5 = 12§ km/h. Let b = boat speed in still water, c = current speed. Then: §b + c = 20§ and §b - c = 12§. Subtracting the equations: §(b+c) - (b-c) = 20 - 12§ → §2c = 8§ → §c = 4§ km/h.',
    },
    {
      type: 'timed',
      timeLimit: 140,
      question:
        'A train travels §300§ km at §60§ km/h, then §250§ km at §50§ km/h. What is the average speed for the entire journey?',
      options: [
        '§53§ km/h',
        '§54§ km/h',
        '§55§ km/h',
        '§56§ km/h',
        '§57§ km/h',
      ],
      correct: 2,
      explanation:
        '§Time₁ = 300/60 = 5§ hours. §Time₂ = 250/50 = 5§ hours. Total distance = §550§ km. Total time = §10§ hours. Average speed = §550/10 = 55§ km/h.',
    },
    {
      type: 'timed',
      timeLimit: 125,
      question:
        'Two cyclists start from the same point. One travels north at 12 mph, the other east at 16 mph. After 3 hours, how far apart are they?',
      options: ['60 miles', '84 miles', '36 miles', '48 miles', '72 miles'],
      correct: 0,
      explanation:
        'After 3 hours: north = §36§ miles, east = §48§ miles. Distance apart = §\\sqrt{36² + 48²} = \\sqrt{1296 + 2304} = \\sqrt{3600} = 60§ miles.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'A person drives to work at 40 mph and returns at 60 mph. If the one-way distance is 30 miles, what is the average speed for the round trip?',
      options: ['48 mph', '50 mph', '52 mph', '54 mph', '56 mph'],
      correct: 0,
      explanation:
        'Time going = §30/40 = 0.75§ hours. Time returning = §30/60 = 0.5§ hours. Total time = 1.25 hours. Total distance = 60 miles. Average speed = §60/1.25 = 48§ mph.',
    },
    {
      type: 'timed',
      timeLimit: 140,
      question:
        'Car A leaves a city at 9 AM traveling at 50 mph. Car B leaves the same city at 11 AM traveling in the same direction at 70 mph. At what time does Car B catch up to Car A?',
      options: ['2 PM', '3 PM', '4 PM', '5 PM', '6 PM'],
      correct: 2,
      explanation:
        'In 2 hours (9 AM to 11 AM), Car A travels 100 miles. Relative speed = §70-50 = 20§ mph. Time to catch up = §100/20 = 5§ hours. Car B catches up 5 hours after 11 AM = 4 PM.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'A train travels §300§ km. For the first §100§ km, it travels at §60§ km/h. For the remaining §200§ km, it travels at §80§ km/h. What is the average speed for the entire trip?',
      options: [
        '§70§ km/h',
        '§71§ km/h',
        '§72§ km/h',
        '§73§ km/h',
        '§74§ km/h',
      ],
      correct: 2,
      explanation:
        '§Time₁ = 100/60 = 5/3§ hours. §Time₂ = 200/80 = 5/2§ hours. Total time = §5/3 + 5/2 = 10/6 + 15/6 = 25/6§ hours. Average speed = §300 / (25/6) = 300 \\times 6/25 = 1800/25 = 72§ km/h.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'Two runners on a §400§-meter circular track run in the same direction. Runner A runs at §6§ m/s and Runner B runs at §4§ m/s. How long until A laps B?',
      options: [
        '100 seconds',
        '150 seconds',
        '200 seconds',
        '250 seconds',
        '300 seconds',
      ],
      correct: 2,
      explanation:
        'Relative speed = §6 - 4 = 2§ m/s. A must gain 400m on B. Time = §400/2 = 200§ seconds.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'A plane flies 1200 miles with a tailwind of 30 mph in 3 hours. What is the planes speed in still air?',
      options: ['360 mph', '370 mph', '380 mph', '390 mph', '400 mph'],
      correct: 1,
      explanation:
        'Speed with tailwind = §1200/3 = 400§ mph. This equals plane speed + wind. Plane speed = §400 - 30 = 370§ mph.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A runner completes a marathon (§42§ km) in 3.5 hours. What is the average speed in km/h?',
      options: [
        '§12§ km/h',
        '§14§ km/h',
        '§10§ km/h',
        '§15§ km/h',
        '§11§ km/h',
      ],
      correct: 0,
      explanation: 'Average speed = §Distance / Time = 42 / 3.5 = 12§ km/h.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A car travels at §80§km/h for §2.5§ hours. How far does it travel?',
      options: ['§200§ km', '§180§ km', '§220§ km', '§160§ km', '§240§ km'],
      correct: 0,
      explanation: 'Distance = §Rate \\times Time = 80 \\times 2.5 = 200§ km.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'If two cars start 600 miles apart and drive toward each other at 50 mph and 70 mph, when will they meet?',
      options: ['5 hours', '4 hours', '6 hours', '4.5 hours', '5.5 hours'],
      correct: 0,
      explanation:
        'Combined speed = §50 + 70 = 120§ mph. Time = §600/120 = 5§ hours.',
    },
    {
      type: 'untimed',
      question:
        'A person drives to work at 40 mph and returns at 60 mph. If the one-way distance is 30 miles, what is the average speed for the round trip?',
      options: ['48 mph', '50 mph', '52 mph', '54 mph', '56 mph'],
      correct: 0,
      explanation:
        'Time going = §30/40 = 0.75§ hours. Time returning = §30/60 = 0.5§ hours. Total time = 1.25 hours. Total distance = 60 miles. Average speed = §60/1.25 = 48§ mph.',
    },
    {
      type: 'untimed',
      question:
        'Car A leaves a city at 9 AM traveling at 50 mph. Car B leaves the same city at 11 AM traveling in the same direction at 70 mph. At what time does Car B catch up to Car A?',
      options: ['2 PM', '3 PM', '4 PM', '5 PM', '6 PM'],
      correct: 2,
      explanation:
        'In 2 hours (9 AM to 11 AM), Car A travels 100 miles. Relative speed = §70-50 = 20§ mph. Time to catch up = §100/20 = 5§ hours. Car B catches up 5 hours after 11 AM = 4 PM.',
    },
    {
      type: 'untimed',
      question:
        'A train travels 300 km. For the first 100 km, it travels at 60 km/h. For the remaining 200 km, it travels at 80 km/h. What is the average speed for the entire trip?',
      options: ['70 km/h', '71 km/h', '72 km/h', '73 km/h', '74 km/h'],
      correct: 2,
      explanation:
        '§Time₁ = 100/60 = 5/3§ hours. §Time₂ = 200/80 = 5/2§ hours. Total time = §5/3 + 5/2 = 10/6 + 15/6 = 25/6§ hours. Average speed = §300 / (25/6) = 300 \\times 6/25 = 1800/25 = 72§ km/h.',
    },
    {
      type: 'untimed',
      question:
        'Two runners on a 400-meter circular track run in the same direction. Runner A runs at 6 m/s and Runner B runs at 4 m/s. How long until A laps B?',
      options: [
        '100 seconds',
        '150 seconds',
        '200 seconds',
        '250 seconds',
        '300 seconds',
      ],
      correct: 2,
      explanation:
        'Relative speed = §6 - 4 = 2§ m/s. A must gain 400m on B. Time = §400/2 = 200§ seconds.',
    },
    {
      type: 'untimed',
      question:
        'A plane flies 1200 miles with a tailwind of 30 mph in 3 hours. What is the planes speed in still air?',
      options: ['360 mph', '370 mph', '380 mph', '390 mph', '400 mph'],
      correct: 1,
      explanation:
        'Speed with tailwind = §1200/3 = 400§ mph. This equals plane speed + wind. Plane speed = §400 - 30 = 370§ mph.',
    },
    {
      type: 'untimed',
      question:
        'A runner completes a marathon (42 km) in 3.5 hours. What is the average speed in km/h?',
      options: ['12 km/h', '14 km/h', '10 km/h', '15 km/h', '11 km/h'],
      correct: 0,
      explanation: 'Average speed = §Distance / Time = 42 / 3.5 = 12§ km/h.',
    },
    {
      type: 'untimed',
      question:
        'A car travels at 80 km/h for 2.5 hours. How far does it travel?',
      options: ['200 km', '180 km', '220 km', '160 km', '240 km'],
      correct: 0,
      explanation: 'Distance = §Rate \\times Time = 80 \\times 2.5 = 200§ km.',
    },
    {
      type: 'untimed',
      question:
        'If two cars start 600 miles apart and drive toward each other at 50 mph and 70 mph, when will they meet?',
      options: ['5 hours', '4 hours', '6 hours', '4.5 hours', '5.5 hours'],
      correct: 0,
      explanation:
        'Combined speed = §50 + 70 = 120§ mph. Time = §600/120 = 5§ hours.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question:
        'Car A travels 150 miles in 3 hours. Car B travels 180 miles in 4 hours. Compare the two quantities.',
      quantityA: 'Car A average speed',
      quantityB: 'Car B average speed',
      correct: 0,
      explanation:
        'Compare the two quantities directly. Quantity A is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'Which formulas are equivalent to §distance = rate * time§? Select all that apply.',
      options: ['§r = d/t§', '§t = d/r§', '§d = rt§', '§r = t/d§', '§d = r/t§'],
      correct: [0, 1, 2],
      explanation: 'Rearranging §d=rt§ gives §r=d/t§ and §t=d/r§.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question:
        'A cyclist travels 45 miles at 15 miles per hour. Enter the time in hours.',
      correctAnswer: '3',
      acceptedAnswers: ['3'],
      explanation: '§time = distance/rate = 45/15 = 3§ hours.',
    },
  ];

  const quickReference = [
    '§D = r \\times t§ (Distance = Rate × Time)',
    'Average speed = §\\frac{\\text{Total distance}}{\\text{Total time}}§',
    'Relative speed (toward) = §S_1 + S_2§',
    'Relative speed (same direction) = §|S_1 - S_2|§',
    'Meeting time = §\\frac{\\text{Distance}}{\\text{Combined speed}}§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Distance/Rate/Time?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Distance, Rate, and Time problems for the GRE Quantitative section',
    aicontext:
      'Master the fundamentals, relative motion, multi-leg journeys, and advanced scenarios',
  };

  return (
    <LessonTemplate
      lessonId="distance-rate"
      lessonTitle="Distance & Rate"
      lessonIcon={<FiNavigation />}
      lessonDescription="Master the fundamentals, relative motion, multi-leg journeys, and advanced scenarios"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="distance-rate" />
      }
    />
  );
};

export default DistanceRateLesson;
